<template>
  <div class="wrap">
    <div style="display: flex;justify-content: flex-end;">
      <div class="legend-icon" style="background-color:#F56C6C ;"></div>
      异常值
      <div class="legend-icon" style="background-color:#909399 ;"></div>
      正常值
    </div>
    <div ref="chartRef" class="chart"></div>
    <el-popover
      v-model="popoverVisible"
      virtual-triggering
      trigger="manual"
      placement="right-start"
      width="120"
    >
      <div
        slot="reference"
        class="popover-anchor"
        :style="{ left: menuX + 'px', top: menuY + 'px' }"
      />
      <div class="menu-item danger" @click="removeFromSelection">
        删除
      </div>
    </el-popover>
  </div>
</template>

<script>
import * as echarts from "echarts";

export default {
  name: "ChartBrush",

  /* ================= props ================= */
  props: {
    options: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      chart: null,

      /** 内部统一结构 */
      data: [],

      /** 选中点：key = time(x) */
      selectedMap: new Map(),

      /** 框选临时 index */
      brushTempIndexSet: new Set(),

      ignoreNextBrush: false,

      /** popover */
      popoverVisible: false,
      menuX: 0,
      menuY: 0,
      activeTime: null
    };
  },

  watch: {
    options: {
      immediate: true,
      deep: true,
      handler(val) {
        if (!val || !Array.isArray(val.metrics)) return;

        // metrics → 内部 data
        this.data = val.metrics.map(item => ({
          time: item.x,
          value: item.y
        }));

        this.$nextTick(() => {
          this.chart && this.render();
        });
      }
    }
  },

  mounted() {
    this.initChart();
    window.addEventListener("click", this.hidePopover);
  },

  beforeDestroy() {
    window.removeEventListener("click", this.hidePopover);
    this.chart && this.chart.dispose();
  },

  methods: {
    /* ================= 初始化 ================= */
    initChart() {
      this.chart = echarts.init(this.$refs.chartRef);
      this.render();
      this.enableBrush();

      /** 框选实时 */
      this.chart.on("brushSelected", params => {
        if (this.ignoreNextBrush) return;

        const batch = params.batch && params.batch[0];
        if (!batch) return;

        this.brushTempIndexSet.clear();

        batch.selected.forEach(item => {
          if (item.seriesIndex === 1) {
            item.dataIndex.forEach(index => {
              this.brushTempIndexSet.add(index);
            });
          }
        });
      });

      /** 框选结束 */
      this.chart.on("brushEnd", () => {
        this.brushTempIndexSet.forEach(index => {
          const d = this.data[index];
          this.selectedMap.set(d.time, d);
        });
        this.render();
        this.ignoreNextBrush = true;
        this.clearBrush();
        this.$nextTick(() => {
          this.ignoreNextBrush = false;
        });
        this.answerChange();
      });

      /** 点点击 */
      this.chart.on("click", params => {
        this.popoverVisible = false;
        if (params.seriesType !== "scatter") return;

        const time = this.data[params.dataIndex].time;
        if (!this.selectedMap.has(time)) return;

        console.log(
          time,
          params.event.event.clientX,
          params.event.event.clientY
        );
        this.activeTime = time;
        this.menuX = params.event.event.clientX;
        this.menuY = params.event.event.clientY;
        requestAnimationFrame(() => {
          this.popoverVisible = true;
        });
      });
    },

    /* ================= 框选控制 ================= */
    enableBrush() {
      this.chart.dispatchAction({
        type: "takeGlobalCursor",
        key: "brush",
        brushOption: {
          brushType: "rect",
          brushMode: "multiple"
        }
      });
    },

    setOptions(data) {
      data.forEach(p => {
        this.selectedMap.set(p.time, p);
      });
      this.answerChange();
    },
    answerChange() {
      const res = Array.from(this.selectedMap.values()).map(i => ({
        x: i.time,
        y: i.value
      }));
      this.$emit("input", res);
    },
    clearBrush() {
      this.chart.dispatchAction({
        type: "brush",
        areas: []
      });
    },

    /* ================= 删除 ================= */
    removeFromSelection() {
      if (this.activeTime !== null) {
        this.selectedMap.delete(this.activeTime);
      }
      this.popoverVisible = false;
      this.render();

      this.answerChange();
    },

    /* ================= 对外 API ================= */
    restoreSelection(points) {
      points.forEach(p => {
        this.selectedMap.set(p.time, p);
      });
      this.render();
    },

    clearSelectedPoints() {
      this.selectedMap.clear();
      this.render();
    },

    getSelectedPoints() {
      return Array.from(this.selectedMap.values());
    },

    hidePopover() {
      this.popoverVisible = false;
    },

    /* ================= 渲染 ================= */
    render() {
      if (!this.chart || !this.data.length) return;

      const { name, unit } = this.options;

      this.chart.setOption({
        grid: {
          left: 80,
          right: 20,
          top: 20,
          bottom: 80
        },
        xAxis: {
          type: "category",
          data: this.data.map(d => d.time)
        },

        yAxis: {
          axisLabel: {
            formatter: `{value}${unit || ""}`
          },
          splitLine: { show: false }
        },

        dataZoom: [{ type: "inside" }, { type: "slider", bottom: 20 }],

        brush: {
          brushType: "rect",
          brushMode: "multiple",
          seriesIndex: [1]
        },

        series: [
          {
            name,
            type: "line",
            data: this.data.map(d => d.value),
            showSymbol: false,
            z: 1
          },
          {
            type: "scatter",
            symbolSize: 14,
            z: 10,
            data: this.data.map(d => ({
              value: d.value,
              itemStyle: {
                color: this.selectedMap.has(d.time) ? "#F56C6C" : "#909399"
              }
            }))
          }
        ]
      });
    }
  }
};
</script>

<style scoped>
.wrap,
.chart {
  width: 100%;
  height: 100%;
  position: relative;
}

.popover-anchor {
  position: fixed;
  width: 0;
  height: 0;
  pointer-events: none;
  z-index: 10000;
}
.legend-icon {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  margin: 0 8px;
}
.menu-item {
  padding: 8px 12px;
  cursor: pointer;
}

.menu-item.danger {
  color: #ef4444;
}

.menu-item:hover {
  background: #f3f4f6;
}

.debug {
  margin-top: 8px;
  font-size: 14px;
  color: #fff;
}
</style>



// WEBPACK FOOTER //
// src/components/ChartBrush.vue